// ============================================================================
// Tipos de dominio del panel (C7). Espejo de las tablas de C2/C4 — si cambia
// una migración, cambia aquí. NO se generan automáticamente a propósito: el
// panel usa un subconjunto pequeño y explícito de columnas.
// ============================================================================

export type EstadoConversacion = "bot" | "humano" | "cerrada";
/**
 * POR QUÉ está en manos de un humano (`conversaciones.motivo_humano`, migr `0052`).
 * `null` = aparcada antes de la 0052 ⇒ se trata como «no sabemos»: la tarjeta no
 * afirma nada. Lo usa T-LAT-7 para distinguir «nadie la ha tomado todavía»
 * (`escalado`/`averia`) de «ya la atiende alguien» (`tomada`).
 */
export type MotivoHumano = "averia" | "escalado" | "tomada" | "esperando";
// ⭐ T-ESP-1 (migr 0065) · `esperando` = el bot pidió un asesor y SIGUE atendiendo
// mientras llega. Está en `humano` sin dueño ⇒ «Esperando asesor» sale solo, por
// `nadieLaHaTomado`: no hace falta tocar ninguna pantalla.
export type Direccion = "in" | "out";
export type AutorMensaje = "cliente" | "bot" | "humano" | "sistema";
/** ⭐ T-CAL-22 · I-183 · `escuchado` = el cliente reprodujo la NOTA DE VOZ (el `played` de
 *  Meta, migr 0087). Solo las notas lo tienen y nada lo baja. */
export type EstadoEntrega = "encolado" | "enviado" | "entregado" | "leido" | "escuchado" | "fallido";
export type TipoMensaje =
  | "texto" | "imagen" | "documento" | "audio" | "video" | "interactivo" | "plantilla" | "sistema"
  // ★ T-CAL-15 (llamada escrita) · I-253 · la llamada de voz, escrita (migr 0090).
  | "llamada";

export interface Etapa {
  id: string;
  tenant_id: string;
  orden: number;
  nombre: string;
  color: string;
  es_final: boolean;
}

export interface Contacto {
  id: string;
  tenant_id: string;
  wa_id: string;
  telefono_e164: string | null;
  nombre: string | null;
  etapa_id: string | null;
  etiquetas: string[];
  opt_in_estado: "sin_registro" | "opt_in" | "opt_out";
  creado_en: string;
  /** T-BAS-0 (R-BAS-01): el nombre que puso EL EQUIPO. ⛔ Es una columna aparte
   *  de `nombre` porque el bot pisa `nombre` en cada mensaje entrante
   *  (`0053:91`): una corrección hecha ahí duraría hasta el siguiente «hola».
   *  Opcional para que los fixtures escritos antes de la 0055 sigan compilando. */
  nombre_equipo?: string | null;
  /** T-BAS-0 (R-BAS-02): el título de la tarjeta del embudo, ≤ 60 letras. */
  titulo?: string | null;
}

/** T-BAS-0 (R-BAS-04): una fila de `chatai.v_embudo` — el contacto MÁS lo que
 *  sus conversaciones ABIERTAS dicen de él. ⚠️ `no_leidos` es la SUMA de las
 *  abiertas (las cerradas no cuentan: I-4) y `conversacion_id` es la abierta más
 *  reciente, que es la que abre el panel lateral. Todo lo de conversación puede
 *  venir nulo: un contacto sin chat abierto sigue en el embudo. */
export interface ContactoEmbudo extends Contacto {
  no_leidos: number;
  ultima_actividad: string | null;
  conversacion_id: string | null;
  estado_conversacion: EstadoConversacion | null;
  /**
   * T-LAT-7 · R-TAR-05 · **QUIÉN la tiene.** Lo añade `v_embudo` en la `0058`.
   * ⛔⛔ ES EL DATO QUE DECIDE «Esperando asesor», y no `motivo_humano` (🔴 H-1 del
   * fiscal, 2-sep-2026): `asignar_conversacion` (`0005:107`) pone el dueño y **no
   * toca `motivo_humano`**, que sólo pasa a `'tomada'` cuando la persona ESCRIBE
   * (`0052 §4`). Sandra pulsa «Tomar el chat» y se va a consultar un precio ⇒ con
   * el predicado viejo la tarjeta seguía roja y primera de la columna, y con la
   * ventana de 24 h vencida (no puede escribir) lo seguiría diciendo PARA SIEMPRE.
   */
  asignada_a?: string | null;
  /**
   * T-LAT-7 (R-TAR-05): el porqué de la conversación abierta más reciente, para
   * que la tarjeta pueda decir a quién dejaste esperando SIN abrir el chat.
   * Lo añade `v_embudo` en la migración `0058`. Opcional a propósito: hasta que
   * esa migración esté aplicada, la vista no lo trae y la tarjeta calla.
   */
  motivo_humano?: MotivoHumano | null;
  /**
   * T-PRE-9 · R-PRE-01 · I-13 · **el previo**: lo último que se dijo en la
   * conversación que esta tarjeta ABRE (`conversacion_id`), ya pasado por el
   * `case` de la vista — un adjunto sin texto viene NOMBRADO («· foto»), nunca
   * «…». Lo añade `v_embudo` en la migración `0059`.
   * ⚠️ `null` = no hay nada que decir (contacto sin chat abierto, o chat sin
   * mensajes) ⇒ **la tarjeta no pinta la línea**. Opcional a propósito: hasta
   * que la `0059` esté aplicada la vista no lo trae, y callar es lo correcto.
   */
  ultimo_texto?: string | null;
  /** T-PRE-9 · R-PRE-01 · quién dijo lo de `ultimo_texto`, para la marca de la
   *  tarjeta («Tú:» / «Bot:» / nada). Sale de la MISMA fila que el previo. */
  ultimo_autor?: AutorMensaje | null;
  /**
   * T-CAL-4 · R-TEM-01 · I-108 · **la temperatura VIGENTE** (con el enfriamiento ya
   * aplicado por la base, con LA misma regla que `temperatura_vigente`). La añade
   * `v_embudo` en la `0070`.
   * ⚠️ `null` = sin calificación, o el negocio no tiene criterios ACTIVOS ⇒ la
   * tarjeta NO pinta chip. Opcional a propósito: sin la `0070` la vista no la trae
   * y callar es lo correcto.
   */
  temperatura?: Temperatura | null;
  /** T-CAL-4 · el porqué en palabras («pidió cotización · busca Sala nube · Manizales»). */
  temperatura_motivo?: string | null;
  /** T-CAL-4 · `true` si la vigente es más fría que la calculada (el cliente dejó de escribir). */
  temperatura_enfriada?: boolean | null;
  /**
   * T-CAL-19 · I-130 · quién movió la etapa por última vez (`v_embudo`, 0074).
   * `equipo` = EL CANDADO: la movió una persona y el bot no la vuelve a mover hasta
   * que alguien la «suelte». Opcional: sin la 0074 la vista no lo trae y la tarjeta
   * no pinta candado (callar es lo correcto).
   */
  etapa_puesta_por?: "bot" | "equipo" | null;
  /**
   * ⭐ T-ANU-10 · I-843 · el anuncio del PRIMER toque (`v_embudo`, 0097): el
   * `source_id` de Meta y su nombre (el puesto por el script de nombres o, si no,
   * el `headline`). `null` = llegó sin anuncio ⇒ la tarjeta no pinta nada.
   */
  anuncio_id?: string | null;
  anuncio_nombre?: string | null;
}

/** ⭐ T-ANU-10 · «Llegó por el anuncio «X»», o null si no llegó por uno. */
export function textoAnuncio(d: { anuncio_id?: string | null; anuncio_nombre?: string | null } | null | undefined): string | null {
  if (!d?.anuncio_id) return null;
  const nombre = (d.anuncio_nombre ?? "").trim() || d.anuncio_id;
  return `Llegó por el anuncio «${nombre}»`;
}

/** T-CAL-4 · R-TEM-01: los tres valores que guarda `chatai.calificaciones`. */
export type Temperatura = "caliente" | "tibio" | "frio";

export interface Mensaje {
  id: number;
  conversacion_id: string;
  direccion: Direccion;
  tipo: TipoMensaje;
  cuerpo: { texto?: string; media_url?: string; evento?: string; motivo?: string | null } & Record<string, unknown>;
  autor: AutorMensaje;
  autor_id: string | null;
  creado_en: string;
  entrega?: EstadoEntrega | null;
  /**
   * C2-D4: lo que mandó el CLIENTE (nota de voz, foto, documento). Va aparte del
   * `cuerpo` porque `mensajes` es append-only y esto cambia: se descarga, se
   * transcribe, se reintenta. Ausente en los mensajes normales.
   */
  media?: MediaMensaje | null;
}

export interface MediaMensaje {
  // ⭐ T-LEY-10e-B (0092): `suprimido` = el original se borró por R-LEY-10; no se firma.
  estado: "pendiente" | "guardado" | "transcrito" | "fallido" | "suprimido";
  storage_path: string | null;
  mime_type: string | null;
  /** Solo audio. Es lo que LEYÓ el bot para responder. */
  transcripcion: string | null;
  /** 0..1. Por debajo de 0,55 el bot confirma en vez de cotizar. */
  confianza: number | null;
  error: string | null;
}

/**
 * T-PAN · Lo que llega por Realtime de `chatai.mensajes_entrega` (la PALOMITA).
 *
 * ⚠️ Va aparte de `Mensaje` a propósito: no es un mensaje, es el ÚNICO campo
 * mutable de su par (0003:81). `mensajes` es append-only por trigger, así que
 * un acuse NUNCA puede llegar como cambio de `mensajes` — este es el único
 * camino por el que la palomita se mueve sin recargar la página.
 */
export interface AcuseDeEntrega {
  mensaje_id: number;
  estado: EstadoEntrega;
}

/**
 * T-PAN · Lo que llega por Realtime de `chatai.mensajes_media` (el estado del
 * ADJUNTO). Es `MediaMensaje` + a qué mensaje pertenece: el hilo lo busca por
 * ese id, porque el evento no trae la conversación.
 */
export interface AcuseDeMedia extends MediaMensaje {
  mensaje_id: number;
}

export interface Conversacion {
  id: string;
  tenant_id: string;
  contacto_id: string;
  canal_id: string;
  estado: EstadoConversacion;
  asignada_a: string | null;
  /** T-LAT-7: viaja en los avisos en vivo de `conversaciones`, para que la
   *  tarjeta del embudo pueda repintar su distintivo sin recargar. */
  motivo_humano?: MotivoHumano | null;
  ventana_expira_en: string | null;
  ultima_actividad: string;
  no_leidos: number;
  contacto?: Contacto;
  ultimo_texto?: string | null;
}

export interface Nota {
  id: string;
  contacto_id: string;
  autor_id: string | null;
  texto: string;
  creado_en: string;
}

// ── T-CAL-2 (R-FIC-01 · R-FIC-02) · la ficha de calificación, SOLO PARA MOSTRAR ──
// La escribe el bot (`chatai_app.anotar_ficha`, migr 0068). El panel la LEE con
// `chatai.ficha_de_contacto(p_tenant, p_contacto)`; la forma de abajo es la del
// `jsonb_build_object` de `0068:231-240`, no una suposición. `frase_origen` puede
// venir NULL (la columna lo admite: lo que anota el equipo puede no tener frase).
export type CampoFicha = "producto" | "espacio" | "ciudad" | "plazo" | "forma_pago" | "presupuesto" | "decide_con";

export interface DatoFicha {
  campo: CampoFicha;
  valor: string;
  frase_origen: string | null;
  puesto_por: "bot" | "equipo";
  actualizado_en: string;
}

export interface FichaCalificacion {
  /** `tenants.calificacion_habilitada`. Apagada ⇒ el bloque no se pinta. */
  habilitada: boolean;
  datos: DatoFicha[];
}

/** Orden FIJO y etiquetas del bloque «Lo que sabemos del cliente». Los siete
 *  campos son los del `check` de `fichas_calificacion.campo` (0068). */
export const CAMPOS_FICHA: { campo: CampoFicha; etiqueta: string }[] = [
  { campo: "producto", etiqueta: "Producto" },
  { campo: "espacio", etiqueta: "Espacio" },
  { campo: "ciudad", etiqueta: "Ciudad" },
  { campo: "plazo", etiqueta: "Para cuándo" },
  { campo: "forma_pago", etiqueta: "Forma de pago" },
  { campo: "presupuesto", etiqueta: "Presupuesto" },
  { campo: "decide_con", etiqueta: "Decide con" },
];

export interface RespuestaRapida {
  id: string;
  atajo: string;
  texto: string;
}

/** Contexto que la app anfitriona (standalone o ERP) le pasa a los componentes. */
export interface ContextoPanel {
  /** Cliente de Supabase ya autenticado. Tipado laxo a propósito: el paquete no
   *  depende de la versión exacta de supabase-js del anfitrión (C7-D1). */
  sb: any;
  tenantId: string;
  usuarioId: string | null;
  rol: "dueno" | "agente" | "victorix";
}

/** ¿La ventana de 24 h sigue abierta? Fuera de ella SOLO pasan plantillas (I-C2-1). */
export function ventanaAbierta(expiraEn: string | null | undefined, ahora = new Date()): boolean {
  if (!expiraEn) return false;
  return new Date(expiraEn).getTime() > ahora.getTime();
}

/** Horas y minutos que le quedan a la ventana (para el indicador del hilo). */
export function restanteVentana(expiraEn: string | null | undefined, ahora = new Date()): string {
  if (!ventanaAbierta(expiraEn, ahora)) return "cerrada";
  const ms = new Date(expiraEn!).getTime() - ahora.getTime();
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
}

/** Reloj humano de la lista: "9:42", "ayer", "12 jul". */
export function fechaCorta(iso: string, ahora = new Date()): string {
  const d = new Date(iso);
  const dias = Math.floor((+new Date(ahora.toDateString()) - +new Date(d.toDateString())) / 86_400_000);
  if (dias === 0) return d.toLocaleTimeString("es-CO", { hour: "numeric", minute: "2-digit" });
  if (dias === 1) return "ayer";
  if (dias < 7) return d.toLocaleDateString("es-CO", { weekday: "short" });
  return d.toLocaleDateString("es-CO", { day: "numeric", month: "short" });
}

/** Nombre a mostrar; nunca "null".
 *  ⭐ T-BAS-0 (R-BAS-01 · I-1): el del EQUIPO manda sobre el de WhatsApp. El
 *  orden es `nombre_equipo` > `nombre` > `telefono_e164` > `wa_id`, y el `trim`
 *  no es cosmético: un nombre de solo espacios tiene que CAER al siguiente, o la
 *  tarjeta sale en blanco y nadie sabe con quién habla. Invertir este orden es
 *  el mutante que mata V-161: el panel seguiría enseñando el número aunque la
 *  asesora ya lo hubiera corregido. */
export function nombreMostrado(c?: Contacto | null): string {
  if (!c) return "Contacto";
  return c.nombre_equipo?.trim() || c.nombre?.trim() || c.telefono_e164 || c.wa_id;
}

/** Iniciales para el avatar (sin fotos: WhatsApp Cloud API no las entrega). */
export function iniciales(nombre: string): string {
  const p = nombre.trim().split(/\s+/).filter(Boolean);
  if (!p.length) return "?";
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

/** C4-D2: una promesa de volver a escribirle a alguien. Fila de `v_seguimientos`. */
export interface Seguimiento {
  id: string;
  tenant_id: string;
  contacto_id: string;
  conversacion_id: string | null;
  vence: string;
  motivo: string;
  mensaje_sugerido: string | null;
  estado: "pendiente" | "hecho" | "cancelado" | "manual";
  creado_por: "bot" | "humano";
  resuelto_en: string | null;
  detalle: Record<string, unknown>;
  creado_en: string;
  wa_id: string;
  telefono_e164: string | null;
  nombre: string | null;
  vencido: boolean;
}

/** C5-D1: espejo de una plantilla de Meta. */
export interface Plantilla {
  id: string;
  nombre_meta: string;
  categoria: "marketing" | "utility" | "auth";
  idioma: string;
  estado_meta: "borrador" | "pendiente" | "aprobada" | "rechazada" | "pausada";
  motivo_rechazo: string | null;
}

export interface Segmento {
  id: string;
  nombre: string;
  filtro: Record<string, unknown>;
}

/** C5-D3: un envío masivo. */
export interface Campania {
  id: string;
  tenant_id: string;
  nombre: string;
  plantilla_id: string;
  segmento_id: string;
  estado: "borrador" | "aprobada" | "programada" | "enviando" | "pausada" | "hecha" | "abortada";
  aprobada_por: string | null;
  aprobada_en: string | null;
  motivo_pausa: string | null;
  tasa_por_min: number;
  creado_en: string;
}

/**
 * C3-D8 + C2-D7: los campos del bot que edita el DUEÑO del negocio.
 *
 * Es la mitad "segura" de la configuración: el prompt, las reglas de precio y
 * los motores son solo-Victorix (RLS, 0004). Aquí solo hay decisiones de trato
 * al cliente, que no pueden romper el motor por mal que se llenen.
 */
export interface PerfilBot {
  tenant_id: string;
  nombre_bot: string;
  cargo: string | null;
  tono: "formal" | "cercano" | "vendedor";
  emojis: boolean;
  firma: string | null;
  idioma: string;
  /** ¿Mostrarle «escribiendo…» al cliente mientras el bot piensa? Incluye el visto azul. */
  indicador_escritura: boolean;
  /** T-ENT-GUION-1 (0088): presentación del negocio y estilo. ⚠️ Aún NO los lee el motor (tramo 2):
   *  por eso NO están en `CAMPOS_DE_IDENTIDAD` (V-19 exige que todo lo de esa lista llegue al prompt). */
  presentacion?: string | null;
  estilo?: string | null;
}

// ── T-ENT-GUION-1 · el guion por secciones y la pestaña «Llamadas» (migr 0088) ──
export type CanalSeccion = "chat" | "llamada" | "ambos";
export interface SeccionGuion {
  id: string; tenant_id: string; orden: number; titulo: string; contenido: string;
  canal: CanalSeccion; activo: boolean; actualizado_en: string;
}
export type LlavePuente = "buscar" | "cotizar" | "consultar" | "foto" | "link";
export interface ConfigLlamadas {
  tenant_id: string;
  saludo: string | null;
  frase_oferta: string | null;
  frases_puente: Partial<Record<LlavePuente, string>>;
  reglas_extra: string | null;
  velocidad: number | null;
}

/**
 * ⭐⭐ LOS CAMPOS DE «QUIÉN ES TU BOT», EN UNA SOLA LISTA DECLARADA
 * (T-IDE-8 · R-IDE-01 / R-IDE-02, 31-ago-2026).
 *
 * ⛔ LA PANTALLA SE PINTA RECORRIENDO ESTA LISTA, no con seis bloques escritos a
 * mano, y eso NO es estética: es lo único que hace medible R-IDE-02 («ningún
 * botón que mienta»). La vara **V-19** (`worker/test/identidad_en_la_pantalla.test.ts`)
 * recorre esta misma constante y exige que CADA campo aparezca en el system
 * prompt que arma `systemDeTenant`. Si la pantalla pintara los campos a mano,
 * V-19 no podría cazar un campo de más — el que se añade bonito, se guarda, y el
 * bot ignora hasta que un cliente lo nota.
 *
 * ⚠️ VIVE EN `tipos.ts` Y NO EN `Ajustes.tsx` A PROPÓSITO: V-19 corre en el
 * runner del worker (`node --test`, sin `npm install` y sin JSX), así que tiene
 * que poder importar este archivo. `tipos.ts` no importa nada y no puede
 * empezar a importar sin romper esa vara.
 *
 * ⚠️ `indicador_escritura` NO está aquí y no es un olvido: no viaja al system
 * prompt —lo lee el worker para mandar el «escribiendo…»— y tiene su propia
 * sección en la pantalla. R-IDE-02 se cumple igual: el motor SÍ lo lee, solo que
 * por otra puerta.
 */
export const CAMPOS_DE_IDENTIDAD = [
  "nombre_bot", "cargo", "tono", "emojis", "firma", "idioma",
] as const;

export type CampoDeIdentidad = typeof CAMPOS_DE_IDENTIDAD[number];

// ── C8 · Alertas de operación (T-ALE-14 / R-ALE-01) ─────────────────────────
export type SeveridadAlerta = "aviso" | "grave" | "recuperado";

/**
 * Una fila de `chatai.alertas`. La tabla es APPEND-ONLY (`0013:36-54`): cada
 * fila es un grito, y `recuperado` cierra el incidente sin borrar nada. Por eso
 * «abierto» no es una columna: es **la última fila de cada `clave`** cuya
 * severidad no sea `recuperado` (ver `alertasAbiertas`).
 *
 * ⛔ `detalle` es OPCIONAL a propósito: para quien no es Victorix la consulta
 * ni siquiera lo pide, así que el motivo técnico no llega al navegador del
 * dueño. No es que se oculte al pintar — es que no viaja (R-ALE-01).
 */
export interface Alerta {
  id: number;
  clave: string;
  severidad: SeveridadAlerta;
  tenant_id: string | null;
  titulo: string;
  creada_en: string;
  detalle?: Record<string, unknown>;
}

// ── CON · El conocimiento del negocio (T-CON-9 · R-CON-01 / R-CON-02) ────────

/**
 * Una fila de `chatai.conocimiento` (`0004_c3_motor.sql:70-82`).
 *
 * ⚠️ `activo` NO es un borrado suave cosmético: es LO ÚNICO que el motor mira
 * (`entrenamiento_db.ts`, `where activo`). Apagar una ficha la saca del bot y
 * deja el texto quieto; borrarla lo pierde. Lo mide **V-33**.
 */
export interface FichaConocimiento {
  id: string;
  tenant_id: string;
  categoria: string;
  titulo: string;
  contenido: string;
  /** Override auditado de la baranda anti-precios de la base (trigger `conocimiento_guard`). */
  permite_precios: boolean;
  activo: boolean;
  actualizado_por: string | null;
  actualizado_en: string;
}

/**
 * ⛔⛔ LOS OCHO TEMAS QUE EL MOTOR SABE COLOCAR EN UN CAMPO PROPIO, Y SU NOMBRE
 * EN ESPAÑOL. **El `titulo` de esta lista es la LLAVE del motor, no una
 * etiqueta**: `worker/src/conectores/entrenamiento.ts:246` reparte cada ficha
 * por su título EXACTO contra `CAMPOS_DE_FICHA`. Renombrar «garantia» a
 * «Garantía» la saca de su campo y el bot deja de saber la garantía **sin un
 * solo error, sin un log y sin que nadie se entere**.
 *
 * ⇒ De ahí las dos reglas que la pantalla obedece y que **V-34** mide:
 *   1. el `titulo` de un tema conocido NO se edita por ningún camino de la
 *      pantalla: se enseña el `nombre` en español y se guarda el `titulo`;
 *   2. esta lista y `CAMPOS_DE_FICHA` son LA MISMA lista. Si se separan, la
 *      pantalla ofrecería un tema que el motor manda a `extra` (o al revés,
 *      dejaría un campo del motor sin dónde escribirse — que es exactamente lo
 *      que pasaba con `web` y `telefono`, meses en `null`, R-CON-01).
 *
 * ⚠️ VIVE EN `tipos.ts` por lo mismo que `CAMPOS_DE_IDENTIDAD`: este archivo no
 * importa nada, así que lo puede leer tanto el panel (jsdom) como el runner del
 * worker. No empezar a importar aquí.
 *
 * Una ficha con un título que NO esté en esta lista es legítima: el motor la
 * mete en `extra` y también la lee. Por eso el dueño puede crear temas propios,
 * y esos SÍ llevan el título libre.
 */
export const TEMAS_CONOCIDOS = [
  { titulo: "direccion", nombre: "Dirección", ayuda: "Dónde queda tu negocio. El bot la da tal cual la escribas." },
  { titulo: "horario", nombre: "Horario de atención", ayuda: "Qué días y a qué horas atiendes." },
  { titulo: "envios", nombre: "Envíos", ayuda: "A dónde envías y en cuánto tiempo llega." },
  { titulo: "medios_pago", nombre: "Medios de pago", ayuda: "Con qué te pueden pagar. Los descuentos por medio de pago van en las reglas de precio, no aquí." },
  { titulo: "garantia", nombre: "Garantía", ayuda: "Qué cubre y por cuánto tiempo." },
  { titulo: "web", nombre: "Página web", ayuda: "La dirección de tu tienda o tu sitio." },
  { titulo: "redes", nombre: "Redes sociales", ayuda: "Instagram, Facebook, TikTok… como quieras que las diga." },
  { titulo: "telefono", nombre: "Teléfono", ayuda: "El número al que pueden llamar." },
] as const;

export type TemaConocido = typeof TEMAS_CONOCIDOS[number]["titulo"];

/** ¿Este título es uno de los ocho que el motor coloca en un campo propio? */
export function esTemaConocido(titulo: string): boolean {
  return TEMAS_CONOCIDOS.some((t) => t.titulo === titulo);
}

// ---------- T-GUI-10: el guion por secciones (R-GUI-01 / R-GUI-03) ----------

/**
 * La fila viva de `prompts_bot`: la versión ACTIVA del guion del inquilino.
 * ⚠️ `bloque_negocio` viaja ENTERO al system del bot (`plantilla_base.ts:158`).
 * Esta pantalla lo parte para MIRARLO; el motor no se entera de las secciones.
 */
export interface GuionActivo {
  id: string;
  version: number;
  activo: boolean;
  bloque_negocio: string;
  creado_por: string | null;
  creado_en: string;
}

/**
 * Las seis secciones del guion, con su nombre en español.
 *
 * ⭐ POR QUÉ SON FIJAS Y CONOCIDAS, igual que los ocho temas de T-CON-9: el
 * valor de R-GUI-01 no es «partir un texto», es que se pueda mirar bloque por
 * bloque y ver que dos reglas se contradicen. Con títulos libres cada inquilino
 * inventaría los suyos y no habría dos guiones comparables.
 *
 * ⚠️ `clave` es lo que se escribe como `### <clave>` dentro del propio
 * `bloque_negocio`: sin tildes y en minúsculas, para que casar sea barato y no
 * dependa de cómo lo teclearon.
 *
 * ⚠️ La especificación (R-GUI-01) decía «cuándo pides la medida». Se cambió a
 * «qué preguntas antes de cotizar»: la medida es de colchones, y estos títulos
 * parten el guion de CUALQUIER inquilino —el colegio y SIA también—. Cambio
 * declarado en `PLAN-T-GUI-10` §7 y pendiente de que Cristhian lo ratifique en
 * la especificación.
 */
export const SECCIONES_GUION = [
  { clave: "quien eres", nombre: "Quién eres",
    ayuda: "Qué es el negocio y qué vende. Es lo que el bot da por sabido." },
  { clave: "como saludas", nombre: "Cómo saludas",
    ayuda: "Cómo abre la conversación cuando alguien escribe por primera vez." },
  { clave: "como presentas un producto", nombre: "Cómo presentas un producto",
    ayuda: "Qué dice y en qué orden: nombre, medidas, precio, foto." },
  { clave: "que preguntas antes de cotizar", nombre: "Qué preguntas antes de cotizar",
    ayuda: "Lo que necesita saber antes de dar un precio, para no cotizar a ciegas." },
  { clave: "cuando pasas a un humano", nombre: "Cuándo pasas a un humano",
    ayuda: "En qué momento deja de contestar y llama a un asesor." },
  { clave: "que nunca haces", nombre: "Qué nunca haces",
    ayuda: "Lo prohibido: inventar precios, prometer plazos, dar datos que no tiene." },
] as const;

/** Minúsculas y sin tildes: «Qué Nunca Haces» y «que nunca haces» son la misma. */
export function claveDeSeccion(titulo: string): string {
  return titulo.trim().toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
}

/** Un bloque del guion tal como se pinta. `texto` puede venir vacío. */
export interface BloqueGuion {
  clave: string;
  nombre: string;
  ayuda?: string;
  /** Uno de los seis conocidos, o un encabezado que escribió alguien. */
  conocido: boolean;
  texto: string;
}

export interface GuionPartido {
  bloques: BloqueGuion[];
  /**
   * Lo que va ANTES del primer encabezado: hoy, el guion entero de Living House
   * (367 caracteres sin un solo `###`). ⛔ No se descarta nunca: descartarlo
   * haría desaparecer de la pantalla el guion vivo sin un solo error. Lo mide
   * V-51.
   */
  sinRepartir: string;
}

/**
 * Parte el guion por sus encabezados `### `.
 *
 * ⛔ POR ENCABEZADOS, NO POR LÍNEAS EN BLANCO (V-50). Partir por párrafos
 * troceaba un mismo bloque en pedazos sueltos, y entonces dos reglas que se
 * contradicen vuelven a quedar mezcladas — que es justo el fallo que R-GUI-01
 * existe para cazar (en GPT Maker, 2.973 caracteres en una sola caja).
 *
 * Los seis conocidos salen SIEMPRE, tengan texto o no (V-52): un bloque vacío
 * es la única forma de que el dueño se entere de que a su bot le falta decir
 * «qué nunca haces».
 */
export function partirGuion(bloqueNegocio: string): GuionPartido {
  const texto = (bloqueNegocio ?? "").replace(/\r\n/g, "\n");
  const lineas = texto.split("\n");
  const encabezado = /^\s{0,3}#{2,4}\s+(.+?)\s*$/;

  const cabecera: string[] = [];
  const encontrados: { clave: string; titulo: string; lineas: string[] }[] = [];

  for (const linea of lineas) {
    const m = encabezado.exec(linea);
    if (m) {
      encontrados.push({ clave: claveDeSeccion(m[1]), titulo: m[1].trim(), lineas: [] });
    } else if (encontrados.length === 0) {
      cabecera.push(linea);
    } else {
      encontrados[encontrados.length - 1].lineas.push(linea);
    }
  }

  const porClave = new Map(encontrados.map((e) => [e.clave, e]));
  const conocidos: BloqueGuion[] = SECCIONES_GUION.map((s) => ({
    clave: s.clave, nombre: s.nombre, ayuda: s.ayuda, conocido: true,
    texto: (porClave.get(s.clave)?.lineas ?? []).join("\n").trim(),
  }));
  const clavesConocidas = new Set<string>(SECCIONES_GUION.map((s) => s.clave));
  const propios: BloqueGuion[] = encontrados
    .filter((e) => !clavesConocidas.has(e.clave))
    .map((e) => ({
      clave: e.clave, nombre: e.titulo, conocido: false,
      texto: e.lineas.join("\n").trim(),
    }));

  return { bloques: [...conocidos, ...propios], sinRepartir: cabecera.join("\n").trim() };
}

// ---------------------------------------------------------------------------
// T-GUI-11 · VERSIONES DEL GUION (R-GUI-02)
// ---------------------------------------------------------------------------

/** Una fila de la lista de versiones. Sin el texto: la lista no lo necesita. */
export interface VersionGuion {
  id: string;
  version: number;
  activo: boolean;
  creado_por: string | null;
  creado_en: string;
}

/**
 * Recompone el texto del guion a partir de sus bloques, con `### <título>`.
 *
 * ⛔⛔ LO PRIMERO QUE VA ES `sinRepartir`, Y NO ES UN DETALLE DE ORDEN. El guion
 * vivo de Living House son 367 caracteres SIN un solo encabezado (medido en
 * producción el 1-sep-2026): si al guardar se recompusieran solo los seis
 * bloques, **el guion entero desaparecería en el primer guardado**, sin un solo
 * error y con la versión nueva marcada como activa. Sería V-51 otra vez, pero
 * escribiendo.
 *
 * ⚠️ Es el inverso de `partirGuion`: los seis conocidos salen con su nombre en
 * español, que `claveDeSeccion` vuelve a convertir en la misma clave («Quién
 * eres» → `quien eres`), y los propios salen con el título que traían. Los
 * vacíos NO se escriben: un `### Qué nunca haces` seguido de nada no es una
 * instrucción, es ruido en el prompt del bot.
 */
export function unirGuion(partido: GuionPartido): string {
  const trozos: string[] = [];
  const cabecera = (partido.sinRepartir ?? "").trim();
  if (cabecera) trozos.push(cabecera);
  for (const b of partido.bloques) {
    const texto = (b.texto ?? "").trim();
    if (!texto) continue;
    trozos.push(`### ${b.nombre}\n${texto}`);
  }
  return trozos.join("\n\n");
}


// ---------- T-CAT-13 · el estado del catálogo DEL BOT (R-CAT-01) -----------

/**
 * El retrato que escribe el worker cada vez que consigue releer el catálogo
 * (`chatai.estado_catalogo`, migración `0054`).
 *
 * ⛔ NO es un conteo del ERP. Contar `erp.v_catalogo_bot` diría «90 productos»
 * aunque el bot lleve horas sin catálogo: mediría el almacén, no lo que el bot
 * tiene en la mano. Lo que la pantalla enseña es esto, y su EDAD.
 */
export interface EstadoCatalogo {
  tenant_id: string;
  productos: number;
  variantes: number;
  fuente: string;
  generado_en: string;
  actualizado_en: string;
}

/** Cada cuánto relee el catálogo el worker (`worker/src/conectores/catalogo_erp.ts:68`). */
export const MINUTOS_REFRESCO_CATALOGO = 2;

/**
 * A partir de cuándo el retrato se considera VIEJO y la pantalla lo dice.
 *
 * ⭐ POR QUÉ 10 Y NO 2. El umbral tiene que llevar HOLGURA sobre el refresco, o
 * la pantalla gritaría en cada tic que llegue tarde por un pico de la base y a
 * un aviso que grita en falso se le deja de hacer caso — que es peor que no
 * tenerlo. Diez minutos son CINCO tics perdidos seguidos: eso ya no es un pico,
 * es que el worker no está pudiendo leer el catálogo.
 */
export const MINUTOS_CATALOGO_VIEJO = 10;

/**
 * Cada cuánto RECALCULA la pantalla la edad del dato con la pestaña abierta.
 *
 * ⛔⛔ POR QUÉ EXISTE ESTE NÚMERO (hallazgo H1 del fiscal, 1-sep-2026). Sin él la
 * edad se evaluaba UNA sola vez, al abrir `/catalogo`: alguien deja el panel
 * abierto, el worker se muere cinco minutos después y la pantalla sigue
 * diciendo «refrescado hace 3 minutos» **indefinidamente**. El aviso de viejo
 * —que es la mitad del tramo— no aparecía nunca sin recargar, y un panel de
 * trabajo se deja abierto todo el día. Es SILENCIO: el sistema diciendo que
 * todo va bien.
 *
 * ⚠️ 30 s y no 60 s (el tic de las alertas): aquí lo que se recalcula es texto
 * ya descargado —cero consultas, cero red—, así que el único costo es un
 * re-render de una pantalla diminuta. Con 60 s el aviso podría tardar un minuto
 * de más en salir; con 30 s, medio.
 * ⚠️ El intervalo NO basta solo: un portátil dormido no dispara temporizadores.
 * Por eso `Catalogo.tsx` recalcula ADEMÁS al volver el foco y al volverse
 * visible la pestaña. V-156.
 */
export const MS_RELOJ_CATALOGO = 30_000;

/**
 * ¿El retrato está viejo? Es la mitad del tramo que evita la pantalla que miente.
 *
 * ⚠️ Se mide contra `generado_en` —cuándo se LEYÓ el catálogo— y no contra
 * `actualizado_en`. Si algún día el worker toca la fila sin haber releído, lo
 * que la persona necesita saber sigue siendo la edad del DATO.
 *
 * ⚪ LÍMITE DECLARADO, NO TAPADO (hallazgo H2 del fiscal, 1-sep-2026): `ahora`
 * es el reloj del NAVEGADOR y `generado_en` lo escribe el servidor. Un equipo
 * con el reloj más de 10 minutos adelantado vería «dato viejo» permanente con
 * el worker sano. Cerrarlo exige un reloj DEL SERVIDOR en el momento de leer
 * —una RPC nueva o una cabecera— y eso es una puerta nueva a la base, no un
 * retoque de pantalla: se deja escrito para que quien lo cierre sepa por dónde,
 * en vez de forzarlo aquí. Con NTP puesto el desfase es raro, y el aviso falla
 * hacia el lado ruidoso (dice «viejo» de más, nunca «fresco» de más).
 */
export function catalogoViejo(generadoEn: string, ahora = new Date()): boolean {
  const t = new Date(generadoEn).getTime();
  // Una fecha ilegible NO se da por fresca: no poder medir la edad es
  // exactamente el caso en el que hay que avisar.
  if (Number.isNaN(t)) return true;
  return ahora.getTime() - t > MINUTOS_CATALOGO_VIEJO * 60_000;
}

// ── T-LAT-7 · R-TAR-05 · LA REGLA DE «¿QUIÉN TIENE ESTE CHAT?», UNA SOLA VEZ ──
// 🔴 POR QUÉ VIVE AQUÍ Y NO EN CADA PANTALLA (H-1 del fiscal, 2-sep-2026). El
// mismo rótulo —«Esperando asesor»— se pinta en TRES sitios: el inbox
// (`Inbox.tsx`), la cabecera del hilo (`Hilo.tsx`) y, desde T-LAT-7, la tarjeta
// del embudo (`Embudo.tsx`). La primera versión del embudo lo decidía con
// `motivo_humano` mientras los otros dos lo decidían con `asignada_a`: **mismo
// texto, misma pinta, otra regla**, y las dos verdades contrarias se veían a 30
// cm una de otra en la misma pantalla. Tres copias de una regla son tres reglas.
//
// ⛔⛔ EL DATO QUE MANDA ES `asignada_a`, Y ESTÁ MEDIDO:
//   · `asignar_conversacion` (`0005_c4_panel.sql:107`) hace
//     `update conversaciones set asignada_a = p_usuario` y **NO toca
//     `motivo_humano`**.
//   · `motivo_humano` sólo pasa a `'tomada'` cuando alguien **ESCRIBE**
//     (`0052 §4`: `v_etiqueta := 'tomada'` cuando hay `sub` en el JWT).
//   ⇒ Entre «tomar» y «escribir» pueden pasar minutos, una consulta de precio, o
//     TODA LA VIDA de la conversación si la ventana de 24 h ya venció y la
//     asesora no puede mandar texto libre.
//
// ⚠️ `motivo_humano` NO se tira: dice el PORQUÉ (`averia` = se rompió nuestro
//    motor · `escalado` = lo pidió el cliente o lo decidió el bot) y eso va al
//    `title` de la tarjeta. Pero no decide quién la tiene.

/**
 * ¿Está en manos de una persona y NADIE la ha tomado todavía?
 *
 * ⛔⛔ `undefined` NO ES `null`, Y ESA LÍNEA ES EL HALLAZGO N-1 (fiscal, ronda 2):
 *   · `null`      = **sé** quién la tiene: nadie ⇒ «Esperando asesor».
 *   · `undefined` = **no sé** quién la tiene ⇒ **NO SE AFIRMA NADA**.
 * 🔴 EL ESCENARIO, y no es hipotético: `ContactoEmbudo.asignada_a` es opcional y
 * `listarEmbudo` lee con `select("*")`. Si el panel sale al aire con la vista
 * VIEJA —la `0058` sin aplicar, o alguien recreando `v_embudo` desde la `0055`—
 * cada fila llega sin la columna, `!undefined` es `true`, y **todas las
 * conversaciones `humano` salen con el chip rojo y primeras de su columna**,
 * incluidas las que ya tienen dueño. Nadie ve un error: la consulta no falla,
 * sólo faltan columnas. Es el 🔴 de la ronda 1 entrando por la puerta de atrás.
 * ⭐ Y la dirección del fallo importa: el predicado de la ronda 1 (que miraba
 * `motivo_humano`) con el dato ausente **callaba**; éste, sin esta guarda,
 * **afirma**. Inventar «esperando» sobre un dato que no tenemos es peor que no
 * decirlo — un aviso que miente deja de mirarse, y con él se pierde el único
 * sitio donde se ve a quien dejaste esperando.
 * ⚠️ `laTieneAlguien` ya callaba sola con `undefined` (`!!undefined === false`):
 *    con el dato ausente la tarjeta no dice NI una cosa NI la otra.
 */
export function nadieLaHaTomado(
  estado: EstadoConversacion | null | undefined,
  asignadaA: string | null | undefined,
): boolean {
  if (asignadaA === undefined) return false;   // no sé quién la tiene: no invento
  return estado === "humano" && !asignadaA;
}

/** ¿Ya tiene dueño en el equipo? */
export function laTieneAlguien(
  estado: EstadoConversacion | null | undefined,
  asignadaA: string | null | undefined,
): boolean {
  return estado === "humano" && !!asignadaA;
}

/** El porqué, en palabras, para el `title` de la tarjeta. `null` cuando no se
 *  sabe (conversación aparcada antes de la `0052`): entonces no se afirma nada. */
export function porqueEnHumano(motivo: MotivoHumano | null | undefined): string | null {
  if (motivo === "averia") return "Se aparcó porque falló nuestro motor";
  if (motivo === "escalado") return "El bot la pasó a una persona";
  if (motivo === "tomada") return "La tomó alguien del equipo";
  if (motivo === "esperando") return "El bot pidió un asesor y sigue atendiendo mientras llega";
  return null;
}

// ── T-PRE-9 · R-PRE-01 · I-13 · EL PREVIO, UNA SOLA REGLA ────────────────────
// 🔴 POR QUÉ VIVE AQUÍ. La regla del previo YA existía en la base desde la `0006`
// (el `case` de `v_inbox`, hoy en `0055:76-88`) y la `0059` la lleva también a
// `v_embudo`. Pero **en vivo no hay vista**: lo que llega por Realtime es un
// INSERT crudo de `mensajes`, y la pantalla tiene que aplicar la misma regla a
// mano. `Inbox.tsx:186` la escribió por su cuenta como
// `String(m.cuerpo?.texto ?? "…")` — y estaba MAL desde el día uno: una foto sin
// pie sale «· foto» al recargar y **«…» en vivo**. Dos verdades para el mismo
// mensaje, en la misma lista, según hubiera o no recargado.
// ⭐ Es el mismo patrón que T-LAT-7 aplicó a «Esperando asesor», tres párrafos
//    más arriba: tres copias de una regla son tres reglas.
// ⛔ ESTA FUNCIÓN ES EL GEMELO EN TypeScript DEL `case` DE LA VISTA. Si una
//    cambia, cambian las dos — y la única forma de que no se separen en silencio
//    es que el orden de las ramas sea el MISMO, rama por rama:
//      sistema → el texto si lo hay → el nombre del tipo → «· archivo».
/**
 * El previo de un mensaje: **el texto manda; si no lo hay, se NOMBRA el tipo.**
 * Nunca devuelve vacío ni «…»: si no se puede decir qué era, dice «· archivo».
 *
 * ⛔⛔ **`null` = ESTO NO ES UN PREVIO, NO TOQUES NADA** — y no es lo mismo que
 * «pintar una línea vacía». Lo devuelve un mensaje `tipo = 'sistema'`, y es el
 * gemelo EXACTO de lo que hace la vista: el `where m.tipo <> 'sistema'` de la
 * `0059` se lleva la fila, el lateral no devuelve ninguna y el previo sale NULO.
 * 🔴 EL CASO REAL (H-2 del fiscal, decidido por Cristhian el 5-sep-2026): el
 * cliente escribe «me sirve el king, cuánto vale», el bot escala y
 * `pasar_a_humano` (`0052:103`) INSERTA un mensaje de sistema encima. Sin este
 * `null`, la tarjeta que acaba de encenderse como «Esperando asesor» —la que más
 * urge leer— diría «· evento del sistema». Su pedido fue textual: «el mensaje
 * último que **enviamos nosotros o envió el cliente**».
 * ⚠️ QUIEN LLAMA A ESTO TIENE QUE MIRAR EL `null` Y NO HACER NADA: dejar el
 *    previo anterior. Escribirlo tal cual en `ultimo_texto` **borraría** la línea,
 *    que es un fallo distinto y también malo.
 *
 * ⚠️ `nullif(…, '')` de la vista se traduce en el `!== ""` de aquí: un `texto`
 *    presente pero VACÍO no es texto, y tiene que caer al nombre del tipo igual
 *    que en la base. Sin eso, una foto con pie vacío diría «» — una línea en
 *    blanco donde tenía que decir «· foto».
 * ⚠️ Y se pasa por `String(...)` como hace `->>` en Postgres: un `texto` que
 *    llegue como número (payload raro de Realtime) se ve, no desaparece.
 */
export function previoDeMensaje(m: Pick<Mensaje, "tipo" | "cuerpo">): string | null {
  if (m.tipo === "sistema") return null;
  const crudo = m.cuerpo?.texto;
  const texto = crudo === null || crudo === undefined ? "" : String(crudo);
  if (texto !== "") return texto;
  switch (m.tipo) {
    case "imagen": return "· foto";
    case "audio": return "· nota de voz";
    case "video": return "· video";
    case "documento": return "· documento";
    case "plantilla": return "· plantilla";
    case "interactivo": return "· mensaje con botones";
    case "llamada": return "· llamada";
    default: return "· archivo";
  }
}

// ---------- T-CAL-5 · los criterios de calificación editables (R-CRI-01/02) ----------
/** La fila de `chatai.criterios_calificacion` que lee el panel. `criterios` es el
 *  jsonb COMPLETO (incluye `etapas`, `dormido_desde`…): la pantalla solo pinta y
 *  manda las llaves de la lista cerrada (`LLAVES_PUNTOS`, umbrales, días…). */
export interface FilaCriterios {
  criterios: Record<string, any>;
  activa: boolean;
  actualizado_en: string;
}

/** Una versión de `chatai.criterios_calificacion_versiones` (sin el jsonb: la
 *  lista solo pinta quién, cuándo y la nota). */
export interface VersionCriterios {
  version: number;
  creado_por: string | null;
  creado_en: string;
  nota: string | null;
}

/** ⭐ T-CAL-21 · el gasto de la voz del bot en el mes de Bogotá (`chatai.voz_del_mes`,
 *  migr 0086). Solo lo leen el dueño y Victorix (I-182). `max_mes` null = el tope que
 *  puso Victorix no se puede leer; `costo_mes_usd` null = ninguna nota trae su costo. */
/** ⭐ T-CAL-22 · I-184 · una fila del cuadro «audio contra texto» (`voz_resultados`, 0087):
 *  los clientes de UN grupo de la A/B en UN momento, en una semana. */
export interface FilaVozResultados {
  grupo_ab: "voz" | "texto";
  momento: "recomendacion" | "compra";
  clientes: number;
  /** de ellos, cuántos escribieron dentro de las 24 h siguientes a su primer mensaje marcado */
  respondieron_24h: number;
  notas: number;
  escuchadas: number;
  /** null = ninguna nota trae costo («no se sabe»), nunca 0 */
  costo_usd: number | null;
}
export interface SemanaVozResultados {
  /** lunes de la semana (Bogotá), `YYYY-MM-DD` */
  semana: string;
  incompleta: boolean;
  filas: FilaVozResultados[];
}
export interface VozResultados {
  semana_en_curso: string;
  /** la más reciente primero; todas las semanas pedidas, `filas` vacía si no hubo casos */
  semanas: SemanaVozResultados[];
}

export interface VozDelMes {
  max_mes: number | null;
  audios_mes: number;
  costo_mes_usd: number | null;
  por_momento: { recomendacion: number; compra: number };
}
